import { Link } from 'react-router-dom';
import { promoOffers, type PromoOffer } from '@/mocks/promos';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface OtherPromosProps {
  currentId: number;
}

const OtherPromos = ({ currentId }: OtherPromosProps) => {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });
  const others = promoOffers.filter((o) => o.id !== currentId);

  if (others.length === 0) return null;

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="px-6 lg:px-12 py-12"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg"
            style={{ background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.3)' }}
          >
            <i className="ri-price-tag-3-line text-sm" style={{ color: '#ff2d78' }} />
          </div>
          <h2 className="font-orbitron font-bold text-xl text-white">Другие акции</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {others.map((offer: PromoOffer, i) => (
            <Link
              key={offer.id}
              to={`/promos/${offer.id}`}
              className="group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300"
              style={{
                border: `1px solid ${offer.color}20`,
                minHeight: 180,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease ${i * 0.1}s, transform 0.5s ease ${i * 0.1}s, border-color 0.3s`,
              }}
            >
              {/* BG image */}
              <img
                src={offer.image}
                alt={offer.title}
                className="absolute inset-0 w-full h-full object-cover object-top opacity-15 transition-transform duration-500 group-hover:scale-105"
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(135deg, rgba(5,5,8,0.92) 0%, rgba(5,5,8,0.6) 100%)' }}
              />
              <div
                className="absolute top-0 right-0 w-48 h-48 opacity-15 rounded-full"
                style={{ background: offer.color, filter: 'blur(60px)', transform: 'translate(30%, -30%)' }}
              />

              {/* Top accent */}
              <div
                className="absolute top-0 left-0 right-0 h-0.5"
                style={{ background: `linear-gradient(90deg, ${offer.color}, ${offer.colorSecondary}, transparent)` }}
              />

              {/* Content */}
              <div className="relative z-10 p-6 flex items-center gap-5">
                <div
                  className="w-16 h-16 flex items-center justify-center rounded-2xl font-orbitron font-black text-xl shrink-0"
                  style={{
                    background: `${offer.color}18`,
                    border: `2px solid ${offer.color}`,
                    color: offer.color,
                    boxShadow: `0 0 16px ${offer.color}30`,
                  }}
                >
                  {offer.discount}
                </div>
                <div className="flex-1 min-w-0">
                  <span
                    className="inline-block px-2.5 py-0.5 rounded-full font-mono text-[9px] font-bold mb-2"
                    style={{ background: offer.badgeColor, color: '#050508' }}
                  >
                    {offer.badge}
                  </span>
                  <h3 className="font-orbitron font-bold text-sm text-white leading-snug mb-1 group-hover:opacity-75 transition-opacity">
                    {offer.title}
                  </h3>
                  <p className="font-mono text-xs" style={{ color: offer.color }}>{offer.subtitle}</p>
                </div>
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 transition-all duration-300 group-hover:translate-x-1"
                  style={{ background: `${offer.color}12`, border: `1px solid ${offer.color}25` }}
                >
                  <i className="ri-arrow-right-line text-sm" style={{ color: offer.color }} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OtherPromos;
